import type { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';

import { useCallback } from 'react';

import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { allLangs, useTranslate } from 'src/locales';

import { FlagIcon } from 'src/components/flag-icon';

// ----------------------------------------------------------------------

export type LanguageToggleProps = Omit<ToggleButtonGroupProps, 'value' | 'onChange'>;

export function LanguageToggle({ sx, ...other }: LanguageToggleProps) {
  const { t, currentLang, onChangeLang } = useTranslate('layout');

  const handleLanguageChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newLanguage: string | null) => {
      if (newLanguage && newLanguage !== currentLang.value) {
        onChangeLang(newLanguage as any);
      }
    },
    [currentLang.value, onChangeLang]
  );

  return (
    <ToggleButtonGroup
      value={currentLang.value}
      exclusive
      onChange={handleLanguageChange}
      aria-label={t('languageToggle.ariaLabel')}
      size="small"
      sx={[
        (theme) => ({
          gap: 0.5,
          '& .MuiToggleButtonGroup-grouped': {
            border: 0,
            borderRadius: `${theme.shape.borderRadius}px !important`,
            '&:not(:first-of-type)': {
              marginLeft: 0,
            },
          },
          '& .MuiToggleButton-root': {
            px: 1.5,
            py: 0.5,
            gap: 0.75,
            color: 'text.secondary',
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'action.hover',
            },
            '&.Mui-selected': {
              color: 'text.primary',
              bgcolor: 'action.selected',
              '&:hover': {
                bgcolor: 'action.selected',
              },
            },
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {allLangs.map((lang) => (
        <ToggleButton key={lang.value} value={lang.value} aria-label={lang.label}>
          <FlagIcon code={lang.countryCode} sx={{ width: 20, height: 20 }} />
          <Box component="span" sx={{ fontSize: '0.875rem' }}>
            {lang.label}
          </Box>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
